import {
  Rng,
  SLOWMO_RATE,
  drainEvents,
  stepGame,
  type Bee,
  type ExplosionCircle,
  type GameState,
  type Hazard,
  type Intent,
  type Projectile,
  type SimEvent,
} from '@cuboom/shared';

/**
 * Full-local simulation fork of OUR OWN online actions — every consequence of
 * the local player's input renders from a real sim the same frame it happens,
 * exactly like offline. The pose-ghost era left the consequences (grapes
 * droplets, pomegranate seeds, beans pods, explosions, damage, craters) on the
 * server at RTT; this rig kills that split entirely.
 *
 * On submit the client clones the lockstep peer (its local copy of the sim)
 * into a forked `GameState` (same terrain buffer, rng re-based from the
 * peer's `rngState`), applies the intent through the engine's own `stepGame`
 * immediately, and steps the fork every render frame. Jump arcs, all twelve
 * weapons' flight, every sub-munition (seeds, droplets, eggs, bees, volley
 * pods), explosions, craters, damage popups, HP, deaths, shake, voices,
 * dialogue and slowmo pacing all run and PLAY LOCALLY (`stepFrame` hands the
 * fork's drained events straight to the FX pass).
 *
 * The peer sim's delayed copy of the same action becomes echo-confirmation —
 * and under lockstep the match is EXACT, not tolerance-guessed: same seed,
 * same input at the same tick ⇒ identical ids, identical carve rims
 * (identity-seeded from the crater), identical dialogue picks. The peer's
 * events run through `filterEvents` before the FX pass: the fork's are
 * suppressed (already played instantly), everything else — the opponent's
 * whole turn — plays from local sim data. Acks are paired FIFO with the
 * fork's own local verdicts — matching verdicts suppress (the p2p ack
 * already made the sound), a disagreement on a position-bearing intent
 * (jump/fire/swap/split/aim) releases the fork: the peer's truth wins,
 * blended over ~120 ms. Slowmo races never release (the drag heartbeat may
 * beat the jump's own apply server-side).
 *
 * Authority adopts min-merge-style every snapshot: HP/ammo/props only move
 * toward the server's values when the server knows MORE (a fork-ahead value
 * from an in-flight action is never undone by a snapshot that predates the
 * server applying it). Opponent bodies are always adopted whole — their
 * actions were never ours to predict. The fork releases when the server moves
 * past our turn (or we locally begin the next one); lingering own shells/bees
 * the server hasn't ended stay fork-owned until then. Lingering hazards after
 * release render from the mirror (snapshot truth).
 *
 * Only genuinely server-sourced things remain: the opponent's decisions,
 * match end, and rare rng-divergence corrections (e.g. cloning before the
 * first post-turn snapshot — one dialogue draw off — which surfaces as a
 * cosmetic rim jitter, absorbed by the echo tolerance).
 */

const TICK = 1 / 60;
/** Accumulator slack: wall-clock dt lands within an ulp of TICK. */
const STEP_EPS = 1e-9;
/**
 * Server events older than this stop matching fork predictions (fork live).
 * Generous: the peer's stamped copy of our action lands a FULL peer-lag plus
 * RTT after the fork played it — if the peer clock trails (see `[clock]`
 * diagnostics), that can stretch to several seconds, and a missed match is a
 * visible DOUBLE-REPLAY of our own shot. Opponent actions can't false-match
 * inside a live fork (only one seat drives per turn).
 */
const ECHO_WINDOW_MS = 6000;
/**
 * After the fork released the mirror is the view: echoes of OUR pre-release
 * play are still eaten for a beat (RTT + peer-lag tails), but short enough
 * that the opponent's own blasts near ours aren't swallowed.
 */
const POST_RELEASE_WINDOW_MS = 2000;
/** Pose blend after a fork release, so there is never a snap-back. */
const BLEND_MS = 120;
/** Server-vs-fork launch drift above this re-bases the fork shell. */
const LAUNCH_ADOPT_DIST = 4;
/** Positional tolerance matching predicted vs echoed blasts. */
const END_EVENT_PAD = 80;
/**
 * Hard cap on the fork's own life: a lost handover or missed turn flip can
 * never pin it. Normal ends (turn advance, divergence, deny) come first.
 */
const FORK_CAP_MS = 45_000;
/**
 * Intents whose local-vs-server verdict mismatch means the fork's POSITIONS
 * are wrong and it must release. Slowmo races (heartbeat vs jump apply order)
 * and no-verdict kinds never release.
 */
const POSITIONAL_KINDS = new Set<Intent['kind']>(['jump', 'fire', 'swap', 'split', 'aim']);

interface QueueEntry {
  kind: Intent['kind'];
  turnIndex: number;
  /** The fork's own verdict for this intent, or null when never fork-simulated (pass). */
  localOk: boolean | null;
}

interface Blend {
  entities: Map<number, { x: number; y: number }>;
  projs: Map<number, { x: number; y: number }>;
  start: number | null;
}

function cloneForFork(mirror: GameState): GameState {
  const { terrain, rng, ...rest } = mirror;
  // Terrain is SHARED by reference: fork carves land in the same buffer the
  // mirror renders, so follow-up pods collide with fresh craters instantly
  // and the server's echo needs suppression, not a second carve.
  const c = structuredClone(rest) as GameState;
  c.terrain = terrain;
  c.rng = new Rng(rng.state);
  c.events = [];
  return c;
}

const cloneProj = (p: Projectile): Projectile => ({ ...p });
const cloneBee = (b: Bee): Bee => ({ ...b });
const cloneHaz = (h: Hazard): Hazard => ({ ...h });

export class SimFork {
  /** The live forked sim — the render view while non-null. */
  state: GameState | null = null;
  private myPlayerId = '';
  /** Server projectile/bee/hazard id → fork id when a transit δ misaligned them. */
  private idMap = new Map<number, number>();
  private ownedProj = new Set<number>();
  private ownedBees = new Set<number>();
  private ownedHaz = new Set<number>();
  /** Sent intents awaiting a server ack, FIFO — paired with the fork's verdicts. */
  private queue: QueueEntry[] = [];
  /** Fork-drained events with wall time, for echo matching. */
  private recent: { ev: SimEvent; at: number }[] = [];
  private acc = 0;
  private lastNow: number | null = null;
  private createdAt: number | null = null;
  /** Mirror turnIndex at clone time — advancing past it ends our chapter. */
  private baseTurn = -1;
  private blend: Blend | null = null;

  live(): boolean {
    return this.state != null;
  }

  setContext(myPlayerId: string): void {
    this.myPlayerId = myPlayerId;
  }

  reset(): void {
    this.state = null;
    this.idMap.clear();
    this.ownedProj.clear();
    this.ownedBees.clear();
    this.ownedHaz.clear();
    this.queue = [];
    this.recent = [];
    this.acc = 0;
    this.lastNow = null;
    this.createdAt = null;
    this.baseTurn = -1;
    this.blend = null;
  }

  /**
   * Apply one of OUR intents: clone from the mirror when this opens the
   * chapter, step it through `stepGame` immediately (the caller then drains
   * the fork's events into the FX pass — same-frame juice, offline parity),
   * record what the fork itself decided, and queue the server ack pairing.
   * Returns the fork's own verdict for this intent (null = no ack seen).
   */
  applyMine(intent: Intent, mirror: GameState): boolean | null {
    if (!this.state) {
      this.state = cloneForFork(mirror);
      this.acc = 0;
      this.lastNow = null;
      this.createdAt = null;
      this.baseTurn = mirror.turnIndex;
    }
    const f = this.state;
    const projBefore = new Set(f.projectiles.map((p) => p.id));
    const beeBefore = new Set(f.bees.map((b) => b.id));
    const hazBefore = new Set(f.hazards.map((h) => h.id));
    stepGame(f, intent);
    const ack = f.events.find((e) => e.type === 'intent_ack');
    const localOk = ack && ack.type === 'intent_ack' ? ack.ok : null;
    this.queue.push({ kind: intent.kind, turnIndex: intent.turnIndex, localOk });
    for (const p of f.projectiles) if (!projBefore.has(p.id)) this.ownedProj.add(p.id);
    for (const b of f.bees) if (!beeBefore.has(b.id)) this.ownedBees.add(b.id);
    for (const h of f.hazards) if (!hazBefore.has(h.id)) this.ownedHaz.add(h.id);
    // NOTE these events into the echo matcher BEFORE the caller drains them:
    // the launch batch (spawn/voice/jump_intent/dialogue) is the fork's first
    // play, and the peer's stamped copy lands seconds later — without this
    // entry the match misses and the launch REPLAYS (the double-fire bug).
    for (const ev of f.events) this.noteForkEvent(ev, performance.now());
    return localOk;
  }

  /**
   * Step the fork one render frame and hand its drained events to `play` —
   * the FX/audio pass, the same frame the sim emitted them. Stalls its scalar
   * timeline while a human opponent drives the aiming phase (timers/phases
   * can't advance without their intent) but keeps flight stepping; releases
   * once our chapter (turn) is done.
   */
  stepFrame(dt: number, now: number, play: (evs: SimEvent[]) => void): void {
    const f = this.state;
    if (!f) return;
    if (this.createdAt == null) this.createdAt = now;
    if (this.lastNow == null) this.lastNow = now;
    if (now - this.createdAt > FORK_CAP_MS) {
      this.release();
      return;
    }

    this.acc = Math.min(this.acc + (f.slowmo ? dt / SLOWMO_RATE : dt), TICK * 10);
    let guard = 0;
    const stalled = this.oppAiming(f);
    while (this.acc >= TICK - STEP_EPS && guard++ < 10) {
      this.acc -= TICK;
      const saved = stalled ? this.scalars(f) : null;
      stepGame(f);
      if (saved) this.restoreScalars(f, saved);
    }
    this.lastNow = now;

    let evs = drainEvents(f);
    if (stalled) evs = evs.filter((e) => e.type !== 'dialogue' && e.type !== 'voice');
    if (evs.length > 0) {
      for (const ev of evs) {
        this.noteForkEvent(ev, now);
        if (ev.type === 'projectile_spawn') this.ownedProj.add(ev.projectile.id);
        if (ev.type === 'bee_spawn') this.ownedBees.add(ev.bee.id);
        if (ev.type === 'hazard_spawn') this.ownedHaz.add(ev.hazard.id);
      }
      play(evs);
    }

    // Our chapter ended: the fork began the next turn (or the server already
    // moved past — handled in onSnapshot). The mirror takes back over.
    if (f.turnIndex > this.baseTurn) this.release();
  }

  /**
   * End the fork, blending the mirror onto its poses for BLEND_MS — the
   * single release path shared by divergence, turn-advance, cap and resync.
   * The echo `recent` list survives (trimmed to the fresh window by the
   * matcher) so in-flight echoes of what the fork already played are still
   * suppressed on the way out.
   */
  release(): void {
    this.beginBlend();
    this.state = null;
    this.ownedProj.clear();
    this.ownedBees.clear();
    this.ownedHaz.clear();
    this.queue = [];
    this.acc = 0;
    this.lastNow = null;
    this.createdAt = null;
    this.baseTurn = -1;
  }

  /** True while the fork waits on a human opponent's decision. */
  private oppAiming(f: GameState): boolean {
    if (f.phase !== 'aiming') return false;
    const active = f.entities.find((e) => e.id === f.activeEntityId);
    if (!active) return false;
    const seat = f.seats.find((s) => s.seat === active.seat);
    if (!seat || seat.playerId == null) return false; // CPU decides deterministically
    return seat.playerId !== this.myPlayerId;
  }

  private scalars(f: GameState): object {
    return {
      phase: f.phase,
      phaseEndsAt: f.phaseEndsAt,
      turnIndex: f.turnIndex,
      activeEntityId: f.activeEntityId,
      allowance: { ...f.allowance },
      turnInfo: { ...f.turnInfo },
      slowmo: f.slowmo,
      slowmoUntilTick: f.slowmoUntilTick,
      cpuAim: f.cpuAim ? { ...f.cpuAim } : null,
    };
  }

  private restoreScalars(f: GameState, s: object): void {
    Object.assign(f, s);
  }

  private noteForkEvent(ev: SimEvent, now: number): void {
    this.recent.push({ ev, at: now });
    // Generous cap: sub-munition storms (30 grapelets + fuses + carves) burn
    // through entries fast, and every unnoted one is a potential echo leak.
    if (this.recent.length > 1200) this.recent.splice(0, this.recent.length - 1200);
  }

  private forkId(serverId: number): number {
    return this.idMap.get(serverId) ?? serverId;
  }

  private recentMatch(now: number, pred: (ev: SimEvent) => boolean): SimEvent | null {
    const win = this.state ? ECHO_WINDOW_MS : POST_RELEASE_WINDOW_MS;
    for (let i = this.recent.length - 1; i >= 0; i--) {
      const r = this.recent[i];
      if (now - r.at > win) break;
      if (pred(r.ev)) return r.ev;
    }
    return null;
  }

  /**
   * Split a server batch: echoes of what the fork already played are
   * suppressed (and folded into the fork's state), everything else — opponent
   * actions, corrections, post-release tails — passes through. Also returns
   * echo carve circles the caller should record for resync WITHOUT re-carving
   * (the fork already carved them, rng-aligned).
   */
  filterEvents(evs: SimEvent[], now: number): { live: SimEvent[]; echoedCarves: ExplosionCircle[] } {
    const live: SimEvent[] = [];
    const echoedCarves: ExplosionCircle[] = [];
    for (const ev of evs) {
      const f = this.state;
      // FIFO ack pairing runs even post-release (queue is short-lived; a
      // cleared queue simply passes the ack through to the deny sound).
      if (ev.type === 'intent_ack') {
        if (this.pairAck(ev)) continue;
        live.push(ev);
        continue;
      }
      if (ev.type === 'match_end') {
        live.push(ev);
        continue;
      }
      const echo = this.isEcho(f, ev, now);
      // Echo or not, fold the authoritative values in: echoes carry the
      // server's hp/id truth, live events are news the fork never had.
      if (f) this.adoptReal(f, ev);
      if (echo) {
        if (ev.type === 'terrain_carved') echoedCarves.push(...ev.circles);
        continue;
      }
      // Tracer: a spawn slipping through while the fork is live means the
      // echo match missed — that spawn plays a SECOND time (replay). If this
      // ever prints for our own weapon, the `recent` note is missing again.
      if (!echo && ev.type === 'projectile_spawn' && f) {
        console.log('[echo-leak] spawn passed', ev.projectile.id, ev.projectile.weaponId);
      }
      live.push(ev);
    }
    return { live, echoedCarves };
  }

  /**
   * Pair a server ack with the oldest queued intent of the same turn.
   * Agreeing verdicts suppress (the fork already played its deny sound /
   * nothing to show for ok); a positional-kind disagreement releases the fork
   * — server truth — and the ack itself passes so the deny sound plays.
   * Returns true when the ack was consumed.
   */
  private pairAck(ev: Extract<SimEvent, { type: 'intent_ack' }>): boolean {
    const head = this.queue[0];
    if (!head || (ev.turnIndex != null && head.turnIndex !== ev.turnIndex)) return false;
    this.queue.shift();
    if (head.localOk === null) return false; // not fork-simulated — server's verdict to show
    if (head.localOk === ev.ok) return true; // same verdict — already played locally
    // Divergence: if it was a position-bearing intent, the fork's world is
    // wrong — release into the blend. (Slowmo/unknown kinds just pass.)
    if (POSITIONAL_KINDS.has(head.kind)) this.release();
    return false;
  }

  /**
   * Fold a server event into the fork so it stays truthful: echoes carry the
   * authoritative hp/id values, live events are news the fork never had.
   */
  private adoptReal(f: GameState, ev: SimEvent): void {
    switch (ev.type) {
      case 'projectile_spawn':
        if (!f.projectiles.some((p) => p.id === ev.projectile.id)) {
          f.projectiles.push(cloneProj(ev.projectile));
        }
        break;
      case 'projectile_gone': {
        const id = this.forkId(ev.projectileId);
        const i = f.projectiles.findIndex((p) => p.id === id);
        if (i >= 0) f.projectiles.splice(i, 1);
        this.ownedProj.delete(id);
        this.ownedProj.delete(ev.projectileId);
        break;
      }
      case 'damage': {
        const e = f.entities.find((x) => x.id === ev.entityId);
        if (e) {
          // min-merge: the server knows MORE only when its value is lower
          // (extra damage the fork hasn't applied yet); never undo local HP.
          e.hp = Math.min(e.hp, ev.hpAfter);
          if (ev.hpAfter <= 0) {
            e.alive = false;
            e.hp = 0;
          }
        }
        break;
      }
      case 'death': {
        const e = f.entities.find((x) => x.id === ev.entityId);
        if (e) {
          e.alive = false;
          e.hp = 0;
        }
        break;
      }
      case 'bee_sting': {
        const e = f.entities.find((x) => x.id === ev.entityId);
        if (e) e.hp = Math.min(e.hp, ev.hpAfter);
        break;
      }
      case 'prop_destroyed': {
        const p = f.props.find((x) => x.id === ev.propId);
        if (p) {
          p.alive = false;
          p.hp = 0;
        }
        break;
      }
      case 'bee_gone': {
        const id = this.forkId(ev.beeId);
        const i = f.bees.findIndex((b) => b.id === id);
        if (i >= 0) f.bees.splice(i, 1);
        this.ownedBees.delete(id);
        this.ownedBees.delete(ev.beeId);
        break;
      }
      case 'hazard_gone': {
        const id = this.forkId(ev.hazardId);
        const i = f.hazards.findIndex((h) => h.id === id);
        if (i >= 0) f.hazards.splice(i, 1);
        this.ownedHaz.delete(id);
        this.ownedHaz.delete(ev.hazardId);
        break;
      }
      default:
        break;
    }
  }

  /** True when this server event is the echo of something the fork already played. */
  private isEcho(f: GameState | null, ev: SimEvent, now: number): boolean {
    switch (ev.type) {
      case 'projectile_spawn': {
        if (!f) return false;
        const s = ev.projectile;
        const m = this.recentMatch(
          now,
          (e) =>
            e.type === 'projectile_spawn' &&
            e.projectile.weaponId === s.weaponId &&
            e.projectile.ownerId === s.ownerId &&
            Math.hypot(e.projectile.x - s.x, e.projectile.y - s.y) < 40,
        );
        if (!m || m.type !== 'projectile_spawn') return false;
        const mine = f.projectiles.find((p) => p.id === m.projectile.id);
        if (mine) {
          if (s.id !== mine.id) {
            this.idMap.set(s.id, mine.id);
            this.ownedProj.delete(mine.id);
            this.ownedProj.add(s.id);
            mine.id = s.id;
          }
          // Transit-δ launch drift (mid-air shots): re-base the fork shell on
          // the server's launch so the flights converge instead of running
          // parallel a few px apart.
          if (Math.hypot(mine.x - s.x, mine.y - s.y) > LAUNCH_ADOPT_DIST) {
            mine.x = s.x;
            mine.y = s.y;
            mine.vx = s.vx;
            mine.vy = s.vy;
          }
        }
        return true;
      }
      case 'projectile_gone':
        return (
          this.recentMatch(
            now,
            (e) => e.type === 'projectile_gone' && this.forkId(e.projectileId) === this.forkId(ev.projectileId),
          ) != null
        );
      case 'explosion':
        return (
          this.recentMatch(
            now,
            (e) =>
              e.type === 'explosion' &&
              e.weaponId === ev.weaponId &&
              Math.hypot(e.x - ev.x, e.y - ev.y) <= e.r + END_EVENT_PAD,
          ) != null
        );
      case 'terrain_carved':
        // Every circle must match a fork carve — a partial match means the
        // server carved something we didn't, so the whole batch is live news.
        return ev.circles.every(
          (c) =>
            this.recentMatch(
              now,
              (e) =>
                e.type === 'terrain_carved' &&
                e.circles.some(
                  (k) => Math.hypot(k.x - c.x, k.y - c.y) < 3 && Math.abs(k.r - c.r) < 2,
                ),
            ) != null,
        );
      case 'damage':
        return (
          this.recentMatch(now, (e) => e.type === 'damage' && e.entityId === ev.entityId) != null
        );
      case 'death':
        return (
          this.recentMatch(now, (e) => e.type === 'death' && e.entityId === ev.entityId) != null
        );
      case 'bounce': {
        const id = this.forkId(ev.projectileId);
        return (
          this.recentMatch(now, (e) => e.type === 'bounce' && this.forkId(e.projectileId) === id) !=
          null
        );
      }
      case 'splash':
        return (
          this.recentMatch(
            now,
            (e) => e.type === 'splash' && Math.hypot(e.x - ev.x, e.y - ev.y) < 16,
          ) != null
        );
      case 'bee_spawn': {
        if (!f) return false;
        const m = this.recentMatch(
          now,
          (e) =>
            e.type === 'bee_spawn' &&
            e.bee.ownerId === ev.bee.ownerId &&
            Math.hypot(e.bee.x - ev.bee.x, e.bee.y - ev.bee.y) < 40,
        );
        if (!m || m.type !== 'bee_spawn') return false;
        const mine = f.bees.find((b) => b.id === m.bee.id);
        if (mine && ev.bee.id !== mine.id) {
          this.idMap.set(ev.bee.id, mine.id);
          mine.id = ev.bee.id;
        }
        return true;
      }
      case 'bee_gone':
        return (
          this.recentMatch(
            now,
            (e) => e.type === 'bee_gone' && this.forkId(e.beeId) === this.forkId(ev.beeId),
          ) != null
        );
      case 'bee_sting':
        return (
          this.recentMatch(now, (e) => e.type === 'bee_sting' && e.entityId === ev.entityId) != null
        );
      case 'hazard_spawn': {
        if (!f) return false;
        const m = this.recentMatch(
          now,
          (e) =>
            e.type === 'hazard_spawn' &&
            e.hazard.kind === ev.hazard.kind &&
            Math.hypot(e.hazard.x - ev.hazard.x, e.hazard.y - ev.hazard.y) < 40,
        );
        if (!m || m.type !== 'hazard_spawn') return false;
        const mine = f.hazards.find((h) => h.id === m.hazard.id);
        if (mine && ev.hazard.id !== mine.id) {
          this.idMap.set(ev.hazard.id, mine.id);
          mine.id = ev.hazard.id;
        }
        return true;
      }
      case 'hazard_gone':
        return (
          this.recentMatch(
            now,
            (e) => e.type === 'hazard_gone' && this.forkId(e.hazardId) === this.forkId(ev.hazardId),
          ) != null
        );
      case 'voice':
        return (
          this.recentMatch(
            now,
            (e) => e.type === 'voice' && e.entityId === ev.entityId && e.kind === ev.kind,
          ) != null
        );
      case 'jump_intent':
        return (
          this.recentMatch(now, (e) => e.type === 'jump_intent' && e.entityId === ev.entityId) !=
          null
        );
      case 'land':
        return this.recentMatch(now, (e) => e.type === 'land' && e.entityId === ev.entityId) != null;
      case 'swap':
        return (
          this.recentMatch(now, (e) => e.type === 'swap' && e.aId === ev.aId && e.bId === ev.bId) !=
          null
        );
      case 'slowmo':
        return this.recentMatch(now, (e) => e.type === 'slowmo' && e.active === ev.active) != null;
      case 'phase':
        return this.recentMatch(now, (e) => e.type === 'phase' && e.phase === ev.phase) != null;
      case 'turn_begin':
        return (
          this.recentMatch(
            now,
            (e) =>
              e.type === 'turn_begin' &&
              e.turnIndex === ev.turnIndex &&
              e.entityId === ev.entityId,
          ) != null
        );
      case 'dialogue':
        return (
          this.recentMatch(
            now,
            (e) =>
              e.type === 'dialogue' &&
              e.entityId === ev.entityId &&
              e.category === ev.category,
          ) != null
        );
      case 'prop_destroyed':
        return (
          this.recentMatch(now, (e) => e.type === 'prop_destroyed' && e.propId === ev.propId) != null
        );
      case 'shake':
        return this.recentMatch(now, (e) => e.type === 'shake') != null;
      default:
        return false;
    }
  }

  /**
   * Reconcile with a fresh mirror snapshot. Scalars only advance when the
   * server moved past our turn (then the fork releases — its chapter is over);
   * otherwise fork values win on anything our in-flight action already
   * decided, and the server wins wherever it knows more. Opponent bodies are
   * always adopted whole.
   */
  onSnapshot(mirror: GameState): void {
    const f = this.state;
    if (!f) return;
    if (mirror.turnIndex > f.turnIndex) {
      this.release();
      return;
    }
    // The peer (lockstep sim) opens its own copy of our burst when the
    // stamped fire lands — adopt it ONLY when the fork doesn't already run
    // one: overwriting would reset the pod clock mid-stream. If the peer's
    // handle exists and ours doesn't, it is the truth to follow.
    if (mirror.volley && !f.volley) f.volley = { ...mirror.volley };

    for (const m of mirror.entities) {
      const e = f.entities.find((x) => x.id === m.id);
      if (!e) continue;
      // min-merge HP: adopt only when the server knows MORE damage.
      e.hp = Math.min(e.hp, m.hp);
      if (!m.alive) {
        e.alive = false;
        e.hp = 0;
      }
      if (!this.isMine(f, m.id)) {
        e.x = m.x;
        e.y = m.y;
        e.vx = m.vx;
        e.vy = m.vy;
        e.script = m.script ? { ...m.script } : null;
        e.onGround = m.onGround;
        e.pose = m.pose;
      }
    }
    for (const t of mirror.teams) {
      const ft = f.teams.find((x) => x.id === t.id);
      if (!ft) continue;
      ft.alive = t.alive;
      const mine = t.playerId != null && t.playerId === this.myPlayerId;
      if (mine) {
        // Our shelf: adopt only decreases (server applied our shot); a
        // snapshot predating it never undoes the local decrement.
        for (const id of Object.keys(ft.ammo) as (keyof typeof ft.ammo)[]) {
          ft.ammo[id] = Math.min(ft.ammo[id], t.ammo[id] ?? ft.ammo[id]);
        }
        ft.swapLeft = Math.min(ft.swapLeft, t.swapLeft);
        // Scores only grow: keep the fork's ahead values, adopt server's if higher.
        ft.score.damageDealt = Math.max(ft.score.damageDealt, t.score.damageDealt);
        ft.score.kills = Math.max(ft.score.kills, t.score.kills);
      } else {
        ft.ammo = { ...t.ammo };
        ft.score = { ...t.score };
        ft.swapLeft = t.swapLeft;
      }
    }
    for (const m of mirror.props) {
      const p = f.props.find((x) => x.id === m.id);
      if (p && !m.alive) {
        p.alive = false;
        p.hp = 0;
      }
    }
    // Array merges are ADD-ONLY: adopt what the server has that we lack
    // (opponent shells, things we missed) and refresh non-owned rows.
    // Fork-owned ids are removed only by their paired `*_gone`/explosion
    // events — a snapshot that predates the server applying our action must
    // never make our shell flicker out of the sky.
    for (const m of mirror.projectiles) {
      if (!f.projectiles.some((p) => p.id === m.id)) f.projectiles.push(cloneProj(m));
    }
    for (const m of mirror.bees) {
      if (!f.bees.some((b) => b.id === m.id)) f.bees.push(cloneBee(m));
    }
    for (const m of mirror.hazards) {
      const h = f.hazards.find((x) => x.id === m.id);
      if (!h) f.hazards.push(cloneHaz(m));
      else if (!this.ownedHaz.has(h.id)) Object.assign(h, cloneHaz(m));
    }
  }

  /** True when this entity belongs to our seat (it is ours to pose). */
  private isMine(f: GameState, entityId: number): boolean {
    const e = f.entities.find((x) => x.id === entityId);
    if (!e) return false;
    const seat = f.seats.find((s) => s.seat === e.seat);
    return seat?.playerId === this.myPlayerId;
  }

  private beginBlend(): void {
    const f = this.state;
    if (!f) return;
    const entities = new Map<number, { x: number; y: number }>();
    for (const e of f.entities) entities.set(e.id, { x: e.x, y: e.y });
    const projs = new Map<number, { x: number; y: number }>();
    for (const p of f.projectiles) projs.set(p.id, { x: p.x, y: p.y });
    this.blend = { entities, projs, start: null };
  }

  blendActive(): boolean {
    return this.blend != null;
  }

  /** Ease the mirror back onto fork poses for ~120 ms after a release. */
  applyBlend(mirror: GameState, now: number): void {
    const b = this.blend;
    if (!b) return;
    if (b.start == null) b.start = now;
    const t = Math.min(1, (now - b.start) / BLEND_MS);
    if (t >= 1) {
      this.blend = null;
      return;
    }
    const k = 1 - t;
    for (const e of mirror.entities) {
      const from = b.entities.get(e.id);
      if (from) {
        e.x += (from.x - e.x) * k;
        e.y += (from.y - e.y) * k;
      }
    }
    for (const p of mirror.projectiles) {
      const from = b.projs.get(p.id);
      if (from) {
        p.x += (from.x - p.x) * k;
        p.y += (from.y - p.y) * k;
      }
    }
  }
}
